import {
  ArrowLeft,
  CalendarDays,
  Download,
  FileText,
  LoaderCircle,
  Phone,
  Search,
  UserRound,
  Users,
  Utensils,
} from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from "react";
import { type TourAgendaItem, type TourDetailResponse, type TourMonth } from "../../src/shared/contracts";
import { getTourDetail, getTourMonths, searchTour } from "./api";

const brandName = "Vietnam Trip!";

type SearchForm = {
  query: string;
  sheetName: string;
};

type TourRouteParams = {
  code: string;
  sheetSlug: string;
  token: string;
};

type FlightSummary = {
  arrivalTime: string;
  departureTime: string;
  destination: string;
  flightNumber: string;
  item: TourAgendaItem;
  origin: string;
  route: string;
};

const shellClass = "mx-auto w-full max-w-6xl px-4";
const panelClass = "rounded-lg border border-slate-200 bg-white";
const inputClass =
  "min-h-12 w-full rounded-lg border border-slate-300 bg-slate-50 px-3.5 text-slate-900 outline-none transition placeholder:font-normal placeholder:italic placeholder:text-slate-400 focus:border-teal-700 focus:ring-4 focus:ring-teal-700/15";
const fieldLabelClass = "grid gap-2 font-bold text-slate-700";
const errorClass = "rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 font-semibold text-red-800";
const labelClass = "text-xs font-bold uppercase tracking-normal text-slate-500";
const valueClass = "mt-1 break-anywhere text-sm font-semibold leading-relaxed text-slate-900";

const cleanText = (value: string) => value.replace(/\u00a0/g, " ").replace(/[ \t]+/g, " ").trim();

const textOrDash = (value: string) => cleanText(value) || "-";

const splitLines = (value: string) =>
  value
    .split(/\r?\n/)
    .map(cleanText)
    .filter(Boolean);

const agendaMonthIndexes: Record<string, number> = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

const createAgendaDate = (year: number, monthIndex: number, day: number) => {
  if (!Number.isInteger(year) || !Number.isInteger(monthIndex) || !Number.isInteger(day)) return null;
  if (day < 1 || day > 31 || monthIndex < 0 || monthIndex > 11) return null;

  const date = new Date(year, monthIndex, day);

  if (date.getFullYear() !== year || date.getMonth() !== monthIndex || date.getDate() !== day) {
    return null;
  }

  return date;
};

const parseAgendaDate = (value: string) => {
  const text = cleanText(value);
  if (!text) return null;

  const vietnameseMatch = text.match(/(\d{1,2})\s+thg\s+(\d{1,2})\s+(\d{4})/i);

  if (vietnameseMatch) {
    return createAgendaDate(Number(vietnameseMatch[3]), Number(vietnameseMatch[2]) - 1, Number(vietnameseMatch[1]));
  }

  const namedMatch = text.match(/(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);

  if (namedMatch) {
    const month = agendaMonthIndexes[namedMatch[2].toLowerCase()];

    if (month !== undefined) {
      return createAgendaDate(Number(namedMatch[3]), month, Number(namedMatch[1]));
    }
  }

  const numericMatch = text.match(/(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/);

  if (numericMatch) {
    return createAgendaDate(Number(numericMatch[3]), Number(numericMatch[2]) - 1, Number(numericMatch[1]));
  }

  return null;
};

const getAgendaDateKey = (value: string) => {
  const parsed = parseAgendaDate(value);

  if (!parsed) return `unknown:${cleanText(value)}`;

  return `${parsed.getFullYear()}-${parsed.getMonth()}-${parsed.getDate()}`;
};

const formatAgendaDateHeading = (value: string) => {
  const parsed = parseAgendaDate(value);

  if (!parsed) return textOrDash(value);

  return parsed.toLocaleDateString("en-US", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatShortAgendaDateHeading = (value: string) => {
  const parsed = parseAgendaDate(value);

  if (!parsed) return textOrDash(value);

  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatMonthDay = (date: Date) =>
  date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
  });

const formatMonthDayYear = (date: Date) =>
  date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

export const formatTourMonthName = (date: Date) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });

const getDefaultTourMonthName = (items: TourMonth[]) => {
  const currentMonth = formatTourMonthName(new Date());

  return items.find((item) => item.name.toLowerCase() === currentMonth.toLowerCase())?.name ?? items[0]?.name ?? "";
};

export const formatTourDateRange = (items: TourAgendaItem[], fallback: string) => {
  const dates = items
    .map((item) => parseAgendaDate(item.fields.date))
    .filter((date): date is Date => Boolean(date))
    .sort((left, right) => left.getTime() - right.getTime());

  if (!dates.length) return textOrDash(fallback);

  const start = dates[0];
  const end = dates.at(-1) ?? start;
  const isSameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  if (isSameDay) return formatMonthDayYear(start);
  if (start.getFullYear() !== end.getFullYear()) return `${formatMonthDayYear(start)} - ${formatMonthDayYear(end)}`;

  return `${formatMonthDay(start)} - ${formatMonthDayYear(end)}`;
};

const parseMeetingTimeMinutes = (value: string) => {
  const text = cleanText(value);
  const match = text.match(/(\d{1,2}):(\d{2})/);

  if (!match) return Number.MAX_SAFE_INTEGER;

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const upper = text.toUpperCase();

  if (upper.includes("PM") && hours < 12) hours += 12;
  if (upper.includes("AM") && hours === 12) hours = 0;

  return hours * 60 + minutes;
};

const compareMeetingTime = (left: string, right: string) =>
  parseMeetingTimeMinutes(left) - parseMeetingTimeMinutes(right);

const isAgendaDateToday = (value: string) => {
  const parsed = parseAgendaDate(value);

  if (!parsed) return false;

  const today = new Date();

  return (
    parsed.getFullYear() === today.getFullYear() &&
    parsed.getMonth() === today.getMonth() &&
    parsed.getDate() === today.getDate()
  );
};

const compareAgendaItems = (left: TourAgendaItem, right: TourAgendaItem) => {
  const leftDate = parseAgendaDate(left.fields.date);
  const rightDate = parseAgendaDate(right.fields.date);

  if (leftDate && rightDate) {
    const difference = leftDate.getTime() - rightDate.getTime();

    if (difference !== 0) return difference;
  } else if (leftDate) {
    return -1;
  } else if (rightDate) {
    return 1;
  } else {
    const dateCompare = cleanText(left.fields.date).localeCompare(cleanText(right.fields.date));

    if (dateCompare !== 0) return dateCompare;
  }

  return left.rowNumber - right.rowNumber;
};

type ScheduleDayGroup = {
  dateLabel: string;
  dayNumber: number;
  isToday: boolean;
  items: TourAgendaItem[];
};

const groupScheduleByDate = (items: TourAgendaItem[]): ScheduleDayGroup[] => {
  const groupMap = new Map<string, TourAgendaItem[]>();

  for (const item of items) {
    const key = getAgendaDateKey(item.fields.date);
    const current = groupMap.get(key);

    if (current) {
      current.push(item);
      continue;
    }

    groupMap.set(key, [item]);
  }

  const grouped = Array.from(groupMap.entries()).map(([key, groupItems]) => {
    const sortedItems = [...groupItems].sort(compareAgendaItems);
    const sampleDate = sortedItems[0]?.fields.date ?? "";
    const parsed = parseAgendaDate(sampleDate);

    return {
      dateKey: key,
      dateLabel: formatAgendaDateHeading(sampleDate),
      isToday: sortedItems.some((item) => isAgendaDateToday(item.fields.date)),
      items: sortedItems,
      sortValue: parsed?.getTime() ?? Number.MAX_SAFE_INTEGER,
    };
  });

  grouped.sort((left, right) => {
    if (left.sortValue !== right.sortValue) return left.sortValue - right.sortValue;

    return left.dateKey.localeCompare(right.dateKey);
  });

  return grouped.map((group, index) => ({
    dateLabel: group.dateLabel,
    dayNumber: index + 1,
    isToday: group.isToday,
    items: group.items,
  }));
};

const safeDecode = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const parseTourRoutePath = (path: string): TourRouteParams | null => {
  const [pathname] = path.split(/[?#]/);
  const match = pathname?.match(/^\/tour\/([^/]+)\/([^/]+)\/([^/]+)\/?$/);

  if (!match) return null;

  return {
    code: safeDecode(match[2] ?? ""),
    sheetSlug: safeDecode(match[1] ?? ""),
    token: safeDecode(match[3] ?? ""),
  };
};

const getTourRouteParams = (): TourRouteParams | null => parseTourRoutePath(window.location.pathname);

const cleanTourDetailUrl = () => {
  if (!window.location.search) return;

  window.history.replaceState(window.history.state, document.title, `${window.location.pathname}${window.location.hash}`);
};

const collectUnique = (items: TourAgendaItem[], keys: Array<keyof TourAgendaItem["fields"]>) => {
  const values = items.flatMap((item) => keys.map((key) => cleanText(item.fields[key]))).filter(Boolean);

  return Array.from(new Set(values)).join("; ");
};

const collectItemValues = (item: TourAgendaItem, keys: Array<keyof TourAgendaItem["fields"]>) =>
  keys.map((key) => cleanText(item.fields[key])).filter(Boolean).join(" | ");

export const normalizeServiceTerminology = (value: string) =>
  cleanText(value)
    .replace(/(^|\s)PVT(?=\s|$)/gi, "$1PRIVATE")
    .replace(/(^|\s)SIC(?=\s|$)/gi, "$1GROUP");

const formatPassengers = (items: TourAgendaItem[]) => {
  const adults = collectUnique(items, ["adult"]);
  const children = collectUnique(items, ["child"]);
  const parts = [adults && `Adult: ${adults}`, children && `Child: ${children}`].filter(Boolean);

  return parts.join(" | ");
};

const normalizeFlightNumber = (value: string) =>
  cleanText(value)
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/^([A-Z]{2})(\d{2,4})$/, "$1 $2");

const flightNumberPattern = /^([A-Z]{2}\s?\d{2,4})$/i;
const flightRoutePattern = /^([A-Z]{3})\s*-\s*([A-Z]{3})$/i;
const flightHeaderPattern = /^([A-Z]{2}\s?\d{2,4})\s*,\s*([A-Z]{3})\s*-\s*([A-Z]{3})$/i;
const flightDatePattern = /^(?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),?\s*)?\d{1,2}\s+[A-Za-z]{3,9}(?:\s+\d{2,4})?$/i;
const flightDateTimePattern = /^(?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),?\s*)?\d{1,2}\s+[A-Za-z]{3,9}(?:\s+\d{2,4})?,\s+\d{1,2}:\d{2}(?:\s*(?:AM|PM))?$/i;
const flightTimeDatePattern = /^\d{1,2}:\d{2}(?:\s*(?:AM|PM))?,\s+(?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),?\s*)?\d{1,2}\s+[A-Za-z]{3,9}(?:\s+\d{2,4})?$/i;
const flightTimeRangePattern = /^(\d{1,2}:\d{2}(?:\s*(?:AM|PM))?)\s*-\s*(\d{1,2}:\d{2}(?:\s*(?:AM|PM))?)$/i;

export const isOwnArrangementDetail = (value: string) =>
  /^(own arrangement|booked with another vendor|own arrangement or booked with another vendor)$/i.test(cleanText(value));

const isFlightDateTime = (value: string) => flightDateTimePattern.test(value) || flightTimeDatePattern.test(value);

const buildFlightSummary = (
  item: TourAgendaItem,
  flightNumber: string,
  origin: string,
  destination: string,
  departureTime: string,
  arrivalTime: string
): FlightSummary => ({
  arrivalTime,
  departureTime,
  destination,
  flightNumber: normalizeFlightNumber(flightNumber),
  item,
  origin,
  route: `${origin}-${destination}`,
});

const extractSemicolonFlightSummary = (item: TourAgendaItem): FlightSummary | null => {
  const parts = item.fields.tourDetailsTimewise.split(";").map(cleanText).filter(Boolean);

  if (parts.length < 4) return null;

  const flightNumberMatch = parts[0]?.match(flightNumberPattern);
  const routeMatch = parts[1]?.match(flightRoutePattern);
  const origin = routeMatch?.[1]?.toUpperCase() ?? "";
  const destination = routeMatch?.[2]?.toUpperCase() ?? "";

  if (!flightNumberMatch || !routeMatch || !origin || !destination) return null;

  if (isFlightDateTime(parts[2]) && isFlightDateTime(parts[3])) {
    return buildFlightSummary(item, flightNumberMatch[1], origin, destination, parts[2], parts[3]);
  }

  const timeRangeMatch = parts[2]?.match(flightTimeRangePattern);

  if (timeRangeMatch && flightDatePattern.test(parts[3])) {
    return buildFlightSummary(
      item,
      flightNumberMatch[1],
      origin,
      destination,
      `${parts[3]}, ${cleanText(timeRangeMatch[1])}`,
      `${parts[3]}, ${cleanText(timeRangeMatch[2])}`
    );
  }

  return null;
};

export const extractFlightSummary = (item: TourAgendaItem): FlightSummary | null => {
  const fields = item.fields;

  if (isOwnArrangementDetail(fields.tourDetailsTimewise)) return null;

  const semicolonSummary = extractSemicolonFlightSummary(item);

  if (semicolonSummary) return semicolonSummary;

  const lines = splitLines(fields.tourDetailsTimewise);

  for (let index = 0; index <= lines.length - 3; index += 1) {
    const headerMatch = lines[index]?.match(flightHeaderPattern);
    const departureTime = lines[index + 1] ?? "";
    const arrivalTime = lines[index + 2] ?? "";

    if (!headerMatch || !isFlightDateTime(departureTime) || !isFlightDateTime(arrivalTime)) {
      continue;
    }

    const origin = headerMatch[2].toUpperCase();
    const destination = headerMatch[3].toUpperCase();

    return buildFlightSummary(item, headerMatch[1], origin, destination, departureTime, arrivalTime);
  }

  return null;
};

const firstLine = (value: string) => splitLines(value)[0] ?? cleanText(value);

const getVisibleDetailLines = (item: TourAgendaItem, summary: FlightSummary | null) => {
  const lines = splitLines(item.fields.tourDetailsTimewise);

  return summary ? [] : lines;
};

export const isLeisureDay = (value: string) => cleanText(value).toLowerCase() === "leisure day";

const isTransferActivity = (item: TourAgendaItem) =>
  /transfer/i.test([item.fields.serviceType, item.fields.tourDetailsTimewise, item.fields.from, item.fields.toTours].join(" "));

const getActivityTitle = (item: TourAgendaItem, summary: FlightSummary | null) => {
  const pickupLocation = firstLine(item.fields.from);
  const dropoff = normalizeServiceTerminology(firstLine(item.fields.toTours));
  const serviceType = normalizeServiceTerminology(item.fields.serviceType);

  if (summary) {
    const prefix = serviceType || "Flight";
    const route = [pickupLocation, dropoff].filter(Boolean).join(" -> ") || summary.route;

    return route ? `${prefix}: ${route}` : prefix;
  }

  if (isTransferActivity(item)) {
    const prefix = serviceType || "Transfer";
    const route = [pickupLocation, dropoff].filter(Boolean).join(" -> ");

    return route ? `${prefix}: ${route}` : prefix;
  }

  return textOrDash(normalizeServiceTerminology(item.fields.toTours));
};

const isOwnServiceType = (value: string) => /^own\b/i.test(cleanText(value));

const detailUrlPattern = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;
const trailingUrlPunctuationPattern = /[),.;:!?]+$/;

const normalizeUrlHref = (value: string) => (value.toLowerCase().startsWith("www.") ? `https://${value}` : value);

const LinkedText = ({ value }: { value: string }) => {
  const parts: ReactNode[] = [];
  let lastIndex = 0;

  Array.from(value.matchAll(detailUrlPattern)).forEach((match) => {
    const rawUrl = match[0];
    const start = match.index ?? 0;
    const end = start + rawUrl.length;
    const trailingMatch = rawUrl.match(trailingUrlPunctuationPattern);
    const trailing = trailingMatch?.[0] ?? "";
    const urlText = trailing ? rawUrl.slice(0, -trailing.length) : rawUrl;

    if (start > lastIndex) parts.push(value.slice(lastIndex, start));

    parts.push(
      <a
        className="font-bold text-teal-800 underline hover:text-teal-950"
        href={normalizeUrlHref(urlText)}
        key={`${start}-${urlText}`}
        rel="noreferrer"
        target="_blank"
      >
        {urlText}
      </a>
    );

    if (trailing) parts.push(trailing);
    lastIndex = end;
  });

  if (lastIndex < value.length) parts.push(value.slice(lastIndex));

  return <>{parts.length ? parts : value}</>;
};

const ContactField = ({ value }: { value: string }) => {
  const contact = cleanText(value);
  const phoneMatch = contact.match(/\+?\d[\d\s-]{7,}/);

  if (!contact) return null;

  return (
    <div className="min-w-0 rounded-lg border border-amber-300 bg-amber-100 p-3">
      <p className={`${labelClass} m-0 flex items-center gap-2 text-amber-950`}>
        <Phone className="size-4" />
        <span>Local contact</span>
      </p>
      {phoneMatch ? (
        <a
          className="m-0 mt-1 inline-block break-anywhere text-base font-extrabold leading-relaxed text-slate-950 hover:text-amber-950"
          href={`tel:${phoneMatch[0].replace(/\s/g, "")}`}
        >
          {contact}
        </a>
      ) : (
        <p className="m-0 mt-1 break-anywhere text-base font-extrabold leading-relaxed text-slate-950">{contact}</p>
      )}
    </div>
  );
};
const InfoLine = ({
  highlight = false,
  icon,
  label,
  value,
}: {
  highlight?: boolean;
  icon?: ReactNode;
  label: string;
  value: string;
}) => (
  <div
    className={
      highlight
        ? "min-w-0 rounded-lg border border-amber-300 bg-amber-100 p-3"
        : "min-w-0 border-t border-slate-100 py-3 first:border-t-0"
    }
  >
    <dt
      className={`flex items-center gap-2 text-xs font-bold uppercase tracking-normal ${
        highlight ? "text-amber-950" : "text-slate-500"
      }`}
    >
      {icon ? <span className={highlight ? "text-amber-950" : "text-teal-700"}>{icon}</span> : null}
      <span>{label}</span>
    </dt>
    <dd className={highlight ? "mt-1 break-anywhere text-base font-extrabold leading-relaxed text-slate-950" : valueClass}>
      {textOrDash(value)}
    </dd>
  </div>
);

const DetailField = ({
  label,
  linkify = false,
  multiline = false,
  value,
}: {
  label: string;
  linkify?: boolean;
  multiline?: boolean;
  value: string;
}) => (
  <div className="min-w-0">
    <p className={`${labelClass} m-0`}>{label}</p>
    <p className={`${valueClass} m-0 ${multiline ? "whitespace-pre-line" : ""}`}>
      {linkify ? <LinkedText value={textOrDash(value)} /> : textOrDash(value)}
    </p>
  </div>
);

const AppHeader = () => (
  <header className={`${shellClass} flex min-h-[88px] items-center py-5`}>
    <a className="inline-flex min-w-0 items-center gap-3.5" href="/" aria-label={`${brandName} home`}>
      <img className="h-20 w-auto shrink-0 rounded-lg border border-slate-200 bg-white" src="/logo.png" alt="" aria-hidden="true" />
      <span className="grid min-w-0 gap-1">
        <span className="text-sm text-slate-500">{brandName}</span>
        <strong className="break-anywhere text-[1.4rem] leading-tight text-slate-900">Tour Lookup</strong>
      </span>
    </a>
  </header>
);

const HomePage = () => {
  const [months, setMonths] = useState<TourMonth[]>([]);
  const [monthsError, setMonthsError] = useState("");
  const [form, setForm] = useState<SearchForm>({ query: "", sheetName: "" });
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  useEffect(() => {
    let isMounted = true;

    getTourMonths()
      .then((items) => {
        if (!isMounted) return;

        setMonths(items);
        setForm((current) => ({
          ...current,
          sheetName: current.sheetName || getDefaultTourMonthName(items),
        }));
      })
      .catch((error: Error) => {
        if (!isMounted) return;

        setMonthsError(error.message);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const canSubmit = Boolean(form.query.trim() && form.sheetName.trim() && !isSearching);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSearchError("");
    setIsSearching(true);

    try {
      const response = await searchTour({ query: form.query, sheetName: form.sheetName });
      window.location.assign(response.detailUrl);
    } catch (error) {
      setSearchError(error instanceof Error ? error.message : "Cannot search this tour.");
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <>
      <AppHeader />
      <main className={`${shellClass} grid gap-5 pb-10 pt-3`}>
        <section className={`${panelClass} p-5 sm:p-6`} aria-labelledby="lookup-title">
          <div className="mb-5 flex items-center gap-2.5 text-teal-700">
            <Search className="size-[22px]" />
            <h1 id="lookup-title" className="m-0 text-2xl font-bold leading-tight text-slate-900">
              Tour Search
            </h1>
          </div>

          <form className="grid gap-4" onSubmit={(event) => void handleSubmit(event)}>
            <label className={fieldLabelClass}>
              <span>Tour code or phone number</span>
              <input
                className={inputClass}
                autoComplete="off"
                name="query"
                placeholder="Example: VN123456 or +84901234567"
                type="text"
                value={form.query}
                onChange={(event) => setForm((current) => ({ ...current, query: event.target.value }))}
              />
            </label>

            <label className={fieldLabelClass}>
              <span>Time</span>
              {months.length > 0 ? (
                <select
                  className={inputClass}
                  name="sheetName"
                  required
                  value={form.sheetName}
                  onChange={(event) => setForm((current) => ({ ...current, sheetName: event.target.value }))}
                >
                  {months.map((month) => (
                    <option key={month.gid} value={month.name}>
                      {month.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className={inputClass}
                  list="month-options"
                  name="sheetName"
                  placeholder="Example: May 2026"
                  required
                  type="text"
                  value={form.sheetName}
                  onChange={(event) => setForm((current) => ({ ...current, sheetName: event.target.value }))}
                />
              )}
            </label>

            <datalist id="month-options">
              {months.map((month) => (
                <option key={month.gid} value={month.name} />
              ))}
            </datalist>

            {monthsError ? <p className={errorClass}>{monthsError}</p> : null}
            {searchError ? <p className={errorClass}>{searchError}</p> : null}

            <button
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-teal-700 bg-teal-700 px-4 font-bold text-white transition hover:bg-teal-800 disabled:border-slate-300 disabled:bg-slate-200 disabled:text-slate-500"
              disabled={!canSubmit}
              type="submit"
            >
              {isSearching ? <LoaderCircle className="size-5 animate-spin" /> : <Search className="size-5" />}
              <span>Search</span>
            </button>
          </form>
        </section>
      </main>
    </>
  );
};

const LoadingDetail = () => (
  <>
    <AppHeader />
    <main className={`${shellClass} pb-10 pt-3`}>
      <section className={`${panelClass} flex min-h-48 items-center justify-center gap-3 p-6 text-slate-700`}>
        <LoaderCircle className="size-6 animate-spin text-teal-700" />
        <strong>Loading tour details...</strong>
      </section>
    </main>
  </>
);

const DetailError = ({ message }: { message: string }) => (
  <>
    <AppHeader />
    <main className={`${shellClass} grid gap-4 pb-10 pt-3`}>
      <a className="inline-flex w-fit items-center gap-2 font-bold text-teal-800 hover:text-teal-950" href="/">
        <ArrowLeft className="size-4" />
        <span>Back to search</span>
      </a>
      <section className={`${panelClass} p-5 sm:p-6`}>
        <p className={errorClass}>{message}</p>
      </section>
    </main>
  </>
);

const ScheduleActivityCard = ({
  emphasized = false,
  item,
  summary,
}: {
  emphasized?: boolean;
  item: TourAgendaItem;
  summary: FlightSummary | null;
}) => {
  const fields = item.fields;
  const isFlight = Boolean(summary);
  const isOwnService = isOwnServiceType(fields.serviceType);
  const isLeisureDayItem = isLeisureDay(fields.tourDetailsTimewise);
  const mealPlan = cleanText(fields.mealPlan);
  const notes = collectItemValues(item, ["specialNote", "notes", "note", "complaints", "solution"]);
  const detailLines = getVisibleDetailLines(item, summary);
  const meetingTime = cleanText(fields.meetingTime);
  const title = getActivityTitle(item, summary);

  return (
    <article
      id={`activity-${item.rowNumber}`}
      className={`grid gap-4 rounded-lg p-4 sm:p-5 ${
        emphasized ? "border border-teal-300 bg-teal-50/70" : "border border-slate-200 bg-white"
      }`}
    >
      {isLeisureDayItem ? (
        <>
          {cleanText(fields.from) ? (
            <h4 className="m-0 break-anywhere text-lg font-bold leading-tight text-slate-950">{fields.from}</h4>
          ) : null}
          <p className="m-0 break-anywhere text-sm font-semibold leading-relaxed text-slate-700">Leisure day</p>
        </>
      ) : (
        <>
          {!isOwnService ? (
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-teal-700 px-3 py-1.5 text-xs font-bold uppercase tracking-normal text-white">
                <CalendarDays className="size-3.5" />
                <span>Pickup Time</span>
              </span>
              <span className="rounded-lg border border-amber-300 bg-amber-100 px-3 py-1.5 text-base font-extrabold text-amber-950">
                {textOrDash(meetingTime)}
              </span>
            </div>
          ) : null}

          <h4 className="m-0 break-anywhere text-lg font-bold leading-tight text-slate-950">{title}</h4>

          {isFlight && summary ? (
            <div className="grid gap-3 rounded-lg border border-sky-200 bg-sky-50 p-3 sm:grid-cols-2 lg:grid-cols-4">
              <DetailField label="Flight" value={summary.flightNumber || firstLine(fields.tourDetailsTimewise)} />
              <DetailField label="Route" value={summary.route} />
              <DetailField label="Depart" value={summary.departureTime} />
              <DetailField label="Arrive" value={summary.arrivalTime} />
            </div>
          ) : null}

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            <DetailField label="Pickup location" value={fields.from} />
            <DetailField label="Drop-off / Tour" value={normalizeServiceTerminology(fields.toTours)} />
            {!isFlight && mealPlan ? <DetailField label="Meal Plan" value={mealPlan} /> : null}
          </div>

          <ContactField value={fields.localContact} />

          {!isFlight && detailLines.length ? <DetailField label="Details" linkify multiline value={detailLines.join("\n")} /> : null}
          {notes ? <DetailField label="Special note" multiline value={notes} /> : null}
        </>
      )}
    </article>
  );
};

const getHighlightedPickupGroup = (items: TourAgendaItem[]) => {
  const dayGroups = groupScheduleByDate(items);
  const todayGroup = dayGroups.find((group) => group.isToday);

  if (!todayGroup) {
    return null;
  }

  const sampleItem = todayGroup.items[0];

  return {
    eyebrow: "Today pickup time",
    heading: `Day ${todayGroup.dayNumber}, ${formatShortAgendaDateHeading(sampleItem?.fields.date ?? "")}`,
    items: todayGroup.items,
  };
};

const HighlightedPickupSection = ({ items }: { items: TourAgendaItem[] }) => {
  const highlighted = getHighlightedPickupGroup(items);

  if (!highlighted) return null;

  return (
    <section
      className="grid gap-4 rounded-lg border border-teal-200 bg-teal-50/60 p-4 sm:p-5"
      aria-labelledby="pickup-title"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-teal-200 pb-3">
        <div>
          <p className="m-0 text-sm font-bold uppercase tracking-wide text-teal-800">{highlighted.eyebrow}</p>
          <h2 id="pickup-title" className="m-0 mt-1 text-xl font-bold leading-tight text-slate-950">
            {highlighted.heading}
          </h2>
        </div>
        <span className="rounded-lg border border-teal-200 bg-white px-3 py-1.5 text-sm font-semibold text-teal-900">
          {highlighted.items.length} {highlighted.items.length === 1 ? "activity" : "activities"}
        </span>
      </div>

      {highlighted.items.map((item) => (
        <ScheduleActivityCard key={item.rowNumber} emphasized item={item} summary={extractFlightSummary(item)} />
      ))}
    </section>
  );
};

const ScheduleSection = ({ items }: { items: TourAgendaItem[] }) => {
  const dayGroups = groupScheduleByDate(items);

  return (
    <section className="grid gap-5" aria-labelledby="schedule-title">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex items-center gap-2.5 text-teal-700">
          <CalendarDays className="size-6" />
          <h2 id="schedule-title" className="m-0 text-2xl font-bold leading-tight text-slate-950">
            Full itinerary
          </h2>
        </div>
      </div>

      {dayGroups.map((group) => (
        <section
          key={`${group.dayNumber}-${group.dateLabel}`}
          className={
            group.isToday
              ? "grid gap-3 rounded-lg border border-teal-200 bg-teal-50/40 p-4 sm:p-5"
              : `${panelClass} grid gap-3 p-4 sm:p-5`
          }
          aria-label={`Day ${group.dayNumber}`}
        >
          <div
            className={`flex flex-wrap items-center gap-2 pb-3 ${
              group.isToday ? "border-b border-teal-200" : "border-b border-slate-200"
            }`}
          >
            <span className="rounded-lg bg-slate-800 px-2.5 py-1 text-sm font-semibold text-white">
              Day {group.dayNumber}
            </span>
            {group.isToday ? (
              <span className="rounded-lg border border-teal-300 bg-teal-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-normal text-teal-900">
                Today
              </span>
            ) : null}
            <h3 className="m-0 text-lg font-bold leading-tight text-slate-950">{group.dateLabel}</h3>
          </div>

          {group.items.map((item) => (
            <ScheduleActivityCard
              key={item.rowNumber}
              emphasized={group.isToday}
              item={item}
              summary={extractFlightSummary(item)}
            />
          ))}
        </section>
      ))}
    </section>
  );
};

const TourInfoPanel = ({
  agent,
  guests,
  meals,
  passengers,
  specialNotes,
}: {
  agent: string;
  guests: string;
  meals: string;
  passengers: string;
  specialNotes: string;
}) => (
  <section className={`${panelClass} grid gap-5 p-5 sm:p-6`} aria-label="Tour information">
    <div>
      <p className="m-0 text-sm font-bold uppercase tracking-normal text-teal-700">Tour information</p>
    </div>

    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <p className="m-0 flex items-center gap-2 text-xs font-bold uppercase tracking-normal text-amber-900">
        <UserRound className="size-4" />
        <span>Guest name & contact</span>
      </p>
      <p className="m-0 mt-2 break-anywhere text-xl font-extrabold leading-tight text-slate-950">{textOrDash(guests)}</p>
    </div>

    <dl className="grid gap-x-6 border-t border-slate-100 pt-5 sm:grid-cols-2 lg:grid-cols-3">
      <InfoLine icon={<Users className="size-4" />} label="Passengers" value={passengers} />
      <InfoLine icon={<FileText className="size-4" />} label="Agent" value={agent} />
      <InfoLine icon={<Utensils className="size-4" />} label="Meal Preferences" value={meals} />
      <InfoLine icon={<FileText className="size-4" />} label="Special requests" value={specialNotes} />
    </dl>
  </section>
);
const DetailPage = ({ routeParams }: { routeParams: TourRouteParams }) => {
  const [detail, setDetail] = useState<TourDetailResponse | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    setDetail(null);
    setError("");

    getTourDetail(routeParams.sheetSlug, routeParams.code, routeParams.token)
      .then((response) => {
        if (!isMounted) return;
        setDetail(response);
      })
      .catch((requestError: Error) => {
        if (!isMounted) return;
        setError(requestError.message);
      });

    return () => {
      isMounted = false;
    };
  }, [routeParams.code, routeParams.sheetSlug, routeParams.token]);

  const scheduleItems = useMemo(() => detail?.items ?? [], [detail]);

  if (error) return <DetailError message={error} />;
  if (!detail) return <LoadingDetail />;

  const guests = collectUnique(detail.items, ["nameOfGuests"]);
  const passengers = formatPassengers(detail.items);
  const meals = collectUnique(detail.items, ["mealPreference"]);
  const agent = collectUnique(detail.items, ["agId"]);
  const specialNotes = collectUnique(detail.items, ["specialNote", "notes", "note", "complaints", "solution"]);
  const tourDateRange = formatTourDateRange(detail.items, detail.sheetName);

  return (
    <>
      <AppHeader />
      <main className={`${shellClass} grid gap-5 pb-10 pt-2`}>
        <a className="inline-flex w-fit items-center gap-2 font-bold text-teal-800 hover:text-teal-950" href="/">
          <ArrowLeft className="size-4" />
          <span>Back to search</span>
        </a>

        <section className="flex flex-wrap items-start justify-between gap-4" aria-labelledby="detail-title">
          <div className="min-w-0">
            <p className="m-0 text-sm font-bold uppercase tracking-normal text-teal-700">Tour details</p>
            <h1 id="detail-title" className="m-0 mt-1 break-anywhere text-3xl font-bold leading-tight text-slate-950">
              {detail.code}
            </h1>
            <p className="m-0 mt-2 text-sm font-semibold text-slate-500">
              {tourDateRange}
            </p>
          </div>
          <a
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-teal-700 bg-teal-700 px-4 font-bold text-white transition hover:bg-teal-800"
            download
            href={detail.agendaDownloadUrl}
          >
            <Download className="size-5" />
            <span>Download PDF</span>
          </a>
        </section>

        <TourInfoPanel
          agent={agent}
          guests={guests}
          meals={meals}
          passengers={passengers}
          specialNotes={specialNotes}
        />

        <HighlightedPickupSection items={scheduleItems} />

        <ScheduleSection items={scheduleItems} />
      </main>
    </>
  );
};

export const App = () => {
  const routeParams = getTourRouteParams();

  useEffect(() => {
    if (!routeParams) return;

    cleanTourDetailUrl();
  }, [routeParams?.code, routeParams?.sheetSlug, routeParams?.token]);

  return routeParams ? <DetailPage routeParams={routeParams} /> : <HomePage />;
};
